import axios, { InternalAxiosRequestConfig } from "axios";
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from "../utils/tokenCookie";

export const OAUTH_BASE_URL =
  process.env.REACT_APP_SC_SERVER_API || "http://localhost:8080";

export const axiosInstance = axios.create({
  baseURL: OAUTH_BASE_URL,
  timeout: 10000,
});

axiosInstance.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers["Authorization-Console"] = `Bearer ${token}`;
  }
  return config;
});

let isRefreshing = false;
let refreshQueue: ((token: string) => void)[] = [];

const flushQueue = (token: string) => {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => {
    const { statusCode, message } = response.data ?? {};
    if (statusCode !== undefined && statusCode !== 1) {
      return Promise.reject(new Error(message || "API 오류가 발생했습니다."));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    if ((status === 401 || status === 403) && !originalRequest._retry) {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearTokens();
        window.location.href = "/login";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push((newToken) => {
            originalRequest.headers["Authorization-Console"] = `Bearer ${newToken}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post<{ accessToken: string; refreshToken: string }>(
          `${OAUTH_BASE_URL}/v1/admin/auth/refresh`,
          { refreshToken }
        );
        setTokens(data.accessToken, data.refreshToken);
        flushQueue(data.accessToken);
        originalRequest.headers["Authorization-Console"] = `Bearer ${data.accessToken}`;
        return axiosInstance(originalRequest);
      } catch {
        clearTokens();
        window.location.href = "/login";
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    const message =
      error.response?.data?.message || error.message || "요청 처리 중 오류가 발생했습니다.";
    return Promise.reject(new Error(message));
  }
);

export const AxiosApi = {
  get: <T = unknown>(url: string, params?: object) =>
    axiosInstance.get<T>(url, { params }).then((r) => r.data),
  post: <T = unknown>(url: string, body?: object) =>
    axiosInstance.post<T>(url, body).then((r) => r.data),
  put: <T = unknown>(url: string, body?: object) =>
    axiosInstance.put<T>(url, body).then((r) => r.data),
  patch: <T = unknown>(url: string, body?: object) =>
    axiosInstance.patch<T>(url, body).then((r) => r.data),
  delete: <T = unknown>(url: string, params?: object) =>
    axiosInstance.delete<T>(url, { params }).then((r) => r.data),
};
